import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadStatusAPI } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, GripVertical, Save } from 'lucide-react';

const COLORS = ['#3B82F6', '#8B5CF6', '#94A3B8', '#64748B', '#EAB308', '#14B8A6', '#06B6D4', '#EF4444', '#22C55E', '#10B981', '#84CC16', '#F59E0B', '#16A34A', '#DC2626', '#6B7280', '#0EA5E9', '#EC4899', '#F97316'];

export const LeadStatusManagementPage = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState({ name: '', color: COLORS[0] });
  const [editing, setEditing] = useState({});
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await leadStatusAPI.list(); setStatuses(data); }
    catch { toast.error('Failed to load statuses'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!newStatus.name.trim()) return toast.error('Name is required');
    try {
      await leadStatusAPI.create({ name: newStatus.name, color: newStatus.color, display_order: statuses.length, is_active: true });
      toast.success('Status added');
      setNewStatus({ name: '', color: COLORS[0] });
      fetch();
    } catch { toast.error('Failed to add'); }
  };

  const handleSaveEdit = async (id) => {
    try { await leadStatusAPI.update(id, editing[id]); toast.success('Saved'); setEditing(e => { const c = { ...e }; delete c[id]; return c; }); fetch(); }
    catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this status? Existing leads with this status will keep the value but it won\'t appear as an option.')) return;
    try { await leadStatusAPI.delete(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Delete failed'); }
  };

  const handleToggleActive = async (s) => {
    try { await leadStatusAPI.update(s._id || s.id, { is_active: !s.is_active }); fetch(); }
    catch { toast.error('Toggle failed'); }
  };

  const move = async (idx, dir) => {
    const newOrder = [...statuses];
    const swap = idx + dir;
    if (swap < 0 || swap >= newOrder.length) return;
    [newOrder[idx], newOrder[swap]] = [newOrder[swap], newOrder[idx]];
    setStatuses(newOrder);
    try { await leadStatusAPI.reorder(newOrder.map(s => s._id || s.id)); toast.success('Reordered'); }
    catch { toast.error('Reorder failed'); fetch(); }
  };

  return (
    <div className="space-y-6" data-testid="status-management-container">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <h1 className="text-3xl font-outfit font-semibold">Lead Status Management</h1>
          <p className="text-muted-foreground">Add, edit, reorder, enable/disable and color-code lead statuses</p>
        </div>
      </div>

      <div className="clay-card p-4">
        <p className="text-sm font-medium mb-2">Add New Status</p>
        <div className="flex flex-wrap gap-2 items-center">
          <input type="text" value={newStatus.name} onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })} placeholder="Status name" data-testid="new-status-name" className="flex-1 min-w-[200px] px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
          <div className="flex gap-1 flex-wrap">
            {COLORS.map(c => (
              <button key={c} onClick={() => setNewStatus({ ...newStatus, color: c })} className={`w-6 h-6 rounded ${newStatus.color === c ? 'ring-2 ring-primary ring-offset-1' : ''}`} style={{ backgroundColor: c }} />
            ))}
          </div>
          <button onClick={handleAdd} data-testid="add-status-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      {loading ? <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div> :
       <div className="clay-card overflow-hidden">
         <table className="w-full text-sm">
           <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
             <tr>{['Order','Name','Color','Active','Actions'].map((h,i)=><th key={i} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">{h}</th>)}</tr>
           </thead>
           <tbody>
             {statuses.map((s, idx) => {
               const sid = s._id || s.id;
               const ed = editing[sid];
               return (
                 <tr key={sid} data-testid={`status-row-${sid}`} className="border-b border-slate-100 dark:border-slate-800">
                   <td className="px-4 py-3">
                     <div className="flex items-center gap-1">
                       <GripVertical className="h-3 w-3 text-muted-foreground" />
                       <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-xs px-1 disabled:opacity-30">▲</button>
                       <button onClick={() => move(idx, 1)} disabled={idx === statuses.length - 1} className="text-xs px-1 disabled:opacity-30">▼</button>
                     </div>
                   </td>
                   <td className="px-4 py-3">
                     {ed ? <input value={ed.name ?? s.name} onChange={(e) => setEditing({ ...editing, [sid]: { ...ed, name: e.target.value } })} className="px-2 py-1 border rounded text-sm bg-white dark:bg-slate-900" /> : <span className="font-medium">{s.name}</span>}
                   </td>
                   <td className="px-4 py-3">
                     <div className="flex items-center gap-1 flex-wrap">
                       {(ed ? COLORS : [ed?.color || s.color]).map(c => (
                         <button key={c} onClick={() => ed && setEditing({ ...editing, [sid]: { ...ed, color: c } })} className={`w-5 h-5 rounded ${(ed ? ed.color === c || (!ed.color && s.color === c) : true) ? 'ring-2 ring-slate-400 ring-offset-1' : ''}`} style={{ backgroundColor: c }} />
                       ))}
                     </div>
                   </td>
                   <td className="px-4 py-3">
                     <button onClick={() => handleToggleActive({ ...s, id: sid })} data-testid={`toggle-${sid}`} className={`text-xs px-2 py-0.5 rounded ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{s.is_active ? 'Active' : 'Disabled'}</button>
                   </td>
                   <td className="px-4 py-3">
                     <div className="flex gap-1">
                       {ed ? (
                         <>
                           <button onClick={() => handleSaveEdit(sid)} className="p-1.5 bg-primary text-white rounded" title="Save"><Save className="h-3 w-3" /></button>
                           <button onClick={() => setEditing(e => { const c = { ...e }; delete c[sid]; return c; })} className="p-1.5 bg-slate-100 rounded text-xs">Cancel</button>
                         </>
                       ) : (
                         <button onClick={() => setEditing({ ...editing, [sid]: { name: s.name, color: s.color } })} data-testid={`edit-${sid}`} className="p-1.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">Edit</button>
                       )}
                       <button onClick={() => handleDelete(sid)} data-testid={`delete-status-${sid}`} className="p-1.5 bg-red-50 text-red-700 rounded"><Trash2 className="h-3 w-3" /></button>
                     </div>
                   </td>
                 </tr>
               );
             })}
           </tbody>
         </table>
       </div>}
    </div>
  );
};
export default LeadStatusManagementPage;
